import { FORME_API_HOST } from "@modules/forme/constants";
import {
  AssignableUser,
  CasePatchBody,
  CaseTemplateDetail,
  CaseTemplateSummary,
  HouseholdSearchRow,
  CasesTasksListResponse,
  ChecklistItemUpdate,
  CreateCaseBody,
  CreateCaseResult,
  CreateNoteBody,
  SearchEnvelope,
  TaskPatchBody,
  WorkItemDetail,
  WorkItemKind,
  WorkItemNote,
  WorkItemParams,
} from "./types";

// Direct browser fetch with the tenant token — there is no Next.js proxy route for
// /v1/advisor/atlas/*.
const CASES_TASKS_BASE = `${FORME_API_HOST}/v1/advisor/atlas/command-center`;

const buildQuery = ({ timezone, advisorEmail }: WorkItemParams): string => {
  const params = new URLSearchParams();
  if (timezone) {
    params.append("timezone", timezone);
  }
  if (advisorEmail) {
    params.append("advisor_email", advisorEmail);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
};

// Writes send only the timezone: the backend rejects advisor_email on every write
// endpoint, so admin view-as is read-only.
const writeQuery = ({ timezone }: WorkItemParams): string =>
  buildQuery({ timezone });

const authHeaders = (accessToken: string) => ({
  Authorization: `Bearer ${accessToken}`,
});

const jsonHeaders = (accessToken: string) => ({
  ...authHeaders(accessToken),
  "Content-Type": "application/json",
});

// Carries the status so callers can branch on it. Substring-matching a code out of the
// message also matches a validation message that happens to contain those digits.
class CasesTasksApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "CasesTasksApiError";
    this.status = status;
  }
}

const failed = async (res: Response, what: string): Promise<never> => {
  let message = "";
  try {
    const body = (await res.json()) as { message?: string; error?: string };
    message = body.message ?? body.error ?? "";
  } catch {
    // Non-JSON body (gateway timeout, HTML error page) — the status still surfaces.
  }
  throw new CasesTasksApiError(
    res.status,
    `Failed to ${what}: ${res.status}${message ? ` ${message}` : ""}`
  );
};

// Search, sort and filtering happen client-side (see utils.ts) — the endpoint takes no
// such params.
const getWorkItems = async (
  accessToken: string,
  params: WorkItemParams
): Promise<CasesTasksListResponse> => {
  const res = await fetch(
    `${CASES_TASKS_BASE}/cases-tasks${buildQuery(params)}`,
    { headers: authHeaders(accessToken) }
  );
  if (!res.ok) return failed(res, "fetch cases and tasks");
  const data = (await res.json()) as Partial<CasesTasksListResponse>;
  // Tolerate a partial payload so one missing list doesn't blank the page.
  return { cases: data.cases ?? [], tasks: data.tasks ?? [] };
};

// Shared by the GET and both PATCH responses: the PATCH result is written straight over
// the detail cache, so a missing array there crashes the expanded card.
const normalizeWorkItemDetail = (raw: WorkItemDetail): WorkItemDetail => ({
  ...raw,
  // Only the LIST truncates description, so a detail row never is. Pinned rather than
  // left absent, or the list row's `true` survives the merge in WorkItemDetailBody and
  // permanently disables editing on long descriptions.
  descriptionTruncated: false,
  notes: raw.notes ?? [],
  checklist: raw.checklist ?? [],
  embeddedTasks: raw.embeddedTasks ?? [],
});

const getWorkItemDetail = async (
  accessToken: string,
  kind: WorkItemKind,
  id: string,
  params: WorkItemParams
): Promise<WorkItemDetail> => {
  const res = await fetch(
    `${CASES_TASKS_BASE}/cases-tasks/${kind}/${encodeURIComponent(
      id
    )}${buildQuery(params)}`,
    { headers: authHeaders(accessToken) }
  );
  if (!res.ok) return failed(res, "fetch case or task details");
  return normalizeWorkItemDetail(await res.json());
};

// Both PATCHes return the full detail object — a superset of the list row, so one
// response updates both caches. It is also the only authoritative post-write state: the
// backend serves a nightly snapshot, so refetching returns pre-edit data.
const patchCase = async (
  accessToken: string,
  id: string,
  body: CasePatchBody,
  params: WorkItemParams
): Promise<WorkItemDetail> => {
  const res = await fetch(
    `${CASES_TASKS_BASE}/cases/${encodeURIComponent(id)}${writeQuery(params)}`,
    {
      method: "PATCH",
      headers: jsonHeaders(accessToken),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) return failed(res, "update case");
  return normalizeWorkItemDetail(await res.json());
};

const patchTask = async (
  accessToken: string,
  id: string,
  body: TaskPatchBody,
  params: WorkItemParams
): Promise<WorkItemDetail> => {
  const res = await fetch(
    `${CASES_TASKS_BASE}/tasks/${encodeURIComponent(id)}${writeQuery(params)}`,
    {
      method: "PATCH",
      headers: jsonHeaders(accessToken),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) return failed(res, "update task");
  return normalizeWorkItemDetail(await res.json());
};

// `timezone` matters: the backend stamps the completion date in the caller's zone, so
// without it an advisor ticking a box in the evening can get tomorrow's date.
const patchChecklistItem = async (
  accessToken: string,
  id: string,
  completed: boolean,
  params: WorkItemParams
): Promise<ChecklistItemUpdate> => {
  const res = await fetch(
    `${CASES_TASKS_BASE}/checklist-items/${encodeURIComponent(id)}${writeQuery(
      params
    )}`,
    {
      method: "PATCH",
      headers: jsonHeaders(accessToken),
      body: JSON.stringify({ completed }),
    }
  );
  if (!res.ok) return failed(res, "update checklist item");
  return res.json();
};

// Notes are append-only, so there is a POST but no PATCH.
const postNote = async (
  accessToken: string,
  caseId: string,
  body: CreateNoteBody,
  params: WorkItemParams
): Promise<WorkItemNote> => {
  const res = await fetch(
    `${CASES_TASKS_BASE}/cases/${encodeURIComponent(caseId)}/notes${writeQuery(
      params
    )}`,
    {
      method: "POST",
      headers: jsonHeaders(accessToken),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) return failed(res, "add note");
  return res.json();
};

const getCaseTemplates = async (
  accessToken: string,
  params: WorkItemParams
): Promise<CaseTemplateSummary[]> => {
  const res = await fetch(
    `${CASES_TASKS_BASE}/case-templates${buildQuery(params)}`,
    { headers: authHeaders(accessToken) }
  );
  if (!res.ok) return failed(res, "fetch case templates");
  const data = (await res.json()) as { templates?: CaseTemplateSummary[] };
  return data.templates ?? [];
};

// Returns the object directly, unlike the list which wraps it.
const getCaseTemplateDetail = async (
  accessToken: string,
  templateId: string,
  params: WorkItemParams
): Promise<CaseTemplateDetail> => {
  const res = await fetch(
    `${CASES_TASKS_BASE}/case-templates/${encodeURIComponent(
      templateId
    )}${buildQuery(params)}`,
    { headers: authHeaders(accessToken) }
  );
  if (!res.ok) return failed(res, "fetch case template");
  const data = (await res.json()) as CaseTemplateDetail;
  return { ...data, tasks: data.tasks ?? [] };
};

// Scoped server-side to the advisor's account team, so it can't offer a household the
// create endpoint would then reject.
const getHouseholds = async (
  accessToken: string,
  search: string,
  params: WorkItemParams
): Promise<SearchEnvelope<HouseholdSearchRow>> => {
  const query = buildQuery(params);
  const trimmed = search.trim();
  const separator = query ? "&" : "?";
  const res = await fetch(
    `${CASES_TASKS_BASE}/households${query}${
      trimmed ? `${separator}search=${encodeURIComponent(trimmed)}` : ""
    }`,
    { headers: authHeaders(accessToken) }
  );
  if (!res.ok) return failed(res, "search households");
  const data = (await res.json()) as Partial<
    SearchEnvelope<HouseholdSearchRow>
  >;
  return {
    rows: data.rows ?? [],
    count: data.count ?? 0,
    limit: data.limit ?? 0,
    truncated: data.truncated ?? false,
  };
};

// Creates the case and its template-derived children, so it can take a few seconds.
// Returns a receipt: large templates are chunked, so a later chunk can fail and leave
// the case partially built — `warnings` is how the advisor finds out.
const postCase = async (
  accessToken: string,
  body: CreateCaseBody,
  params: WorkItemParams
): Promise<CreateCaseResult> => {
  const res = await fetch(`${CASES_TASKS_BASE}/cases${writeQuery(params)}`, {
    method: "POST",
    headers: jsonHeaders(accessToken),
    body: JSON.stringify(body),
  });
  if (!res.ok) return failed(res, "create case");
  return res.json();
};

const getAssignableUsers = async (
  accessToken: string,
  search: string,
  params: WorkItemParams
): Promise<SearchEnvelope<AssignableUser>> => {
  const query = buildQuery(params);
  const trimmed = search.trim();
  const separator = query ? "&" : "?";
  const res = await fetch(
    `${CASES_TASKS_BASE}/users${query}${
      trimmed ? `${separator}search=${encodeURIComponent(trimmed)}` : ""
    }`,
    { headers: authHeaders(accessToken) }
  );
  if (!res.ok) return failed(res, "fetch users");
  const data = (await res.json()) as Partial<SearchEnvelope<AssignableUser>>;
  return {
    rows: data.rows ?? [],
    count: data.count ?? 0,
    limit: data.limit ?? 0,
    truncated: data.truncated ?? false,
  };
};

export {
  CasesTasksApiError,
  normalizeWorkItemDetail,
  getWorkItems,
  getWorkItemDetail,
  patchCase,
  patchTask,
  patchChecklistItem,
  postNote,
  getCaseTemplates,
  getCaseTemplateDetail,
  getHouseholds,
  postCase,
  getAssignableUsers,
};
